import { Button, Drawer, Form, message, Space, Tabs, Typography } from "antd";
import { FormInstance } from "antd/lib/form";
import { useMemo, useState } from "react";
import { matchPath, useLocation, useParams } from "react-router-dom";
import TemplateRenderDataProvider from "screens/designStudio/hooks/TemplateRenderDataProvider";
import { useDataListURLIds } from "shared/components/dataListURL/useDataListURLIds";
import { getAllAdIds } from "shared/components/dataListURL/utils";
import useNavigateWithSearch from "shared/hooks/useNavigateWithSearch";
import { useUser } from "shared/hooks/useUser";
import {
  MarketingMaterialPrintDeliveryForm,
  MarketingMaterialTableItem,
  PrintDeliveryMaterialItem,
} from "shared/types/marketingMaterials";
import Preview from "../marketingMaterialDrawer/Preview";
import { useFetchMarketingMaterials } from "./hooks/useFetchMarketingMaterials";
import { useSelectedMarketingMaterial } from "./hooks/useSelectedMarketingMaterial";
import styles from "./PrintDrawer.module.scss";
import { PrintCheckout } from "./printDrawer/PrintCheckout";
import { PrintForm } from "./printDrawer/PrintForm";
import { ROUTES } from "./utils/constants";
import { formatPhoneNumberWithExt } from "utils/helpers.salesEnablement";
import { uniq } from "lodash";
import SelectMarketingMaterialsDrawer from "./printDrawer/SelectMarketingMaterialsDrawer";
import { PlusOutlined } from "@ant-design/icons";
export const PrintDrawer = () => {
  const [form] = Form.useForm();
  const { marketingMaterials } = useFetchMarketingMaterials();
  const { selectedIds: selectedMarketingMaterialsIds, setSelectedItemIds } =
    useDataListURLIds<MarketingMaterialTableItem>(getAllAdIds);
  const navigateWithSearch = useNavigateWithSearch();
  const user = useUser();
  const location = useLocation();
  const { materialId } = useParams();

  // This `confirms` will be used to highlight the invalid tab titles
  const [confirms, setConfirms] = useState<Record<string, "valid" | "invalid">>(
    {},
  );

  const onClose = () => {
    form.resetFields();
    setConfirms({});
    setActiveMaterial(undefined);
    if (location?.state?.origin === "DRAWER" && materialId) {
      navigateWithSearch(ROUTES.edit(materialId));
      return;
    }
    navigateWithSearch(ROUTES.home);
  };

  const [activeMaterial, setActiveMaterial] = useState<string | undefined>(
    materialId ?? selectedMarketingMaterialsIds[0],
  );

  const validateConfirmations = async (material: PrintDeliveryMaterialItem) => {
    await form.validateFields([
      ["materials", material.id, "confirm", "text"],
      ["materials", material.id, "confirm", "images"],
      ["materials", material.id, "confirm", "review"],
    ]);
  };
  const hasAllConfirmationsChecked = (confirms: Record<string, boolean>) => {
    return ["text", "images", "review"].every(key => !!confirms[key]);
  };
  const showErrorMessage = () =>
    message.error(
      "Please check all required boxes to confirm your review and approval.",
    );
  const onNext = async () => {
    const { materials } = form.getFieldsValue(["materials"]) as {
      materials: Record<string, PrintDeliveryMaterialItem>;
    };

    // Why using selectedMarketingMaterialsIds instead of keys(materials)?
    // Because the keys(materials) are not in the same order as the selectedMarketingMaterialsIds
    // and we need to keep the order of the materials in the form.
    const ids = [...selectedMarketingMaterialsIds];
    const currentIdx = ids.findIndex(id => id === (activeMaterial ?? ids[0]));
    const currentMaterial = materials[ids[currentIdx]];

    const isValid = !!currentMaterial.copies && !!currentMaterial.printMode;

    if (!isValid) return;
    const currentMaterialConfirms = form.getFieldValue([
      "materials",
      currentMaterial.id,
      "confirm",
    ]);

    const endOfList = currentIdx + 1 >= ids.length;
    if (endOfList) {
      const invalidMaterialIds = getInvalidMaterialIds(ids, form);
      if (invalidMaterialIds.length > 0) {
        // To show error messages onthe checkboxes
        invalidMaterialIds.forEach(
          async id => await validateConfirmations(materials[id]),
        );
        const [firstInvalidMaterialId] = invalidMaterialIds;

        setActiveMaterial(firstInvalidMaterialId); // first invalid material
        setConfirms(prev => {
          return {
            ...prev,
            ...invalidMaterialIds.reduce((acc, id) => {
              return {
                ...acc,
                [id]: "invalid",
              };
            }, {}),
          };
        });

        showErrorMessage();
        return;
      }

      navigateWithSearch(ROUTES.printCheckout(materialId), {
        state: {
          origin: location?.state?.origin,
        },
      });
      return;
    }

    const isAllValid =
      currentMaterialConfirms &&
      hasAllConfirmationsChecked(currentMaterialConfirms);
    if (!isAllValid) {
      setConfirms(prev => {
        return {
          ...prev,
          [currentMaterial.id]: "invalid",
        };
      });
      await validateConfirmations(currentMaterial).catch(() => {
        showErrorMessage();
      });
      return;
    } else {
      setConfirms(prev => {
        return {
          ...prev,
          [currentMaterial.id]: "valid",
        };
      });
    }

    const nextMaterial = materials[ids[currentIdx + 1]];
    setActiveMaterial(nextMaterial.id);
  };

  const getInvalidMaterialIds = (
    ids: string[],
    form: FormInstance<MarketingMaterialPrintDeliveryForm>,
  ) => {
    return ids.filter(id => {
      const confirms = form.getFieldValue(["materials", id, "confirm"]);
      return !["text", "images", "review"].every(key => !!confirms?.[key]);
    });
  };

  const endOfList = useMemo(() => {
    if (!activeMaterial) return false;

    const activeMaterialIndex =
      selectedMarketingMaterialsIds.indexOf(activeMaterial);
    return activeMaterialIndex + 1 >= selectedMarketingMaterialsIds.length;
  }, [activeMaterial, selectedMarketingMaterialsIds]);

  const getMaterials = ({
    id,
    name,
    templateThumbnail,
    printOption,
  }: MarketingMaterialTableItem) => {
    if (!printOption) return;

    const minCopies = printOption?.costPerCopy[0]?.quantity;
    const minCopiesQuantity =
      (typeof minCopies === "number" ? minCopies : minCopies?.min) ?? 1;
    const initialPrintMode = printOption?.printMode[0];
    const amount =
      minCopiesQuantity *
      (printOption?.costPerCopy[0]?.cost[initialPrintMode] ?? 1);

    return {
      id,
      name,
      templateThumbnail,
      copies: minCopiesQuantity,
      amount,
      printMode: initialPrintMode,
      printOption: printOption,
    };
  };

  const initialValues = useMemo(() => {
    const materialsIds = materialId
      ? [materialId]
      : selectedMarketingMaterialsIds;
    const materials = marketingMaterials.filter(({ id }) =>
      materialsIds.includes(id),
    );
    const { name, email, user_metadata } = user;
    const fullName =
      !!user_metadata?.firstName && !!user_metadata?.lastName
        ? `${user_metadata.firstName} ${user_metadata.lastName}`
        : name;

    return {
      materials: materials.reduce((acc, material) => {
        if (!material.printOption) return acc;
        const updatedmMaterial = getMaterials(material);
        if (!updatedmMaterial) return acc;

        return {
          ...acc,
          [material.id]: updatedmMaterial,
        };
      }, {}),
      shippingData: {
        fullName,
        email: email ?? "",
        phone: formatPhoneNumberWithExt(user_metadata?.phoneNumber),
        country: "United States",
      },
    };
  }, [materialId, selectedMarketingMaterialsIds, marketingMaterials, user]);

  const isPrintVisible = useMemo(
    () =>
      !!matchPath({ path: ROUTES.print(materialId) }, location.pathname) ||
      !!matchPath(
        { path: ROUTES.printCheckout(materialId) },
        location.pathname,
      ),
    [materialId, location.pathname],
  );

  const onDelete = (id: string) => {
    setSelectedItemIds(
      selectedMarketingMaterialsIds.filter(selectedId => selectedId !== id),
    );
  };

  const [isAddVisible, setIsAddVisible] = useState(false);
  const onAdd = () => {
    setIsAddVisible(true);
  };

  const onCloseAdd = () => {
    setIsAddVisible(false);
  };

  const onSelectMaterials = (materials: MarketingMaterialTableItem[]) => {
    const newSelectedIds = uniq([...materials.map(material => material.id)]);
    setSelectedItemIds(newSelectedIds);

    const newMaterialsData = materials.reduce((acc, material) => {
      const updatedMaterial = getMaterials(material);
      if (!updatedMaterial) return acc;

      return {
        ...acc,
        [material.id]: updatedMaterial,
      };
    }, {});

    const { materials: currentMaterials } = form.getFieldsValue();
    const updatedMaterials = {
      ...currentMaterials,
      ...newMaterialsData,
    };

    form.setFieldsValue({
      materials: updatedMaterials,
    });

    onCloseAdd();
  };

  return (
    <Drawer
      title="Order Prints"
      className={styles.drawer}
      width="calc(100vw - 48px)"
      visible={isPrintVisible}
      onClose={onClose}
      destroyOnClose
      afterVisibleChange={() => {
        // NOTE: This is needed because the PrintDrawer mounts when MarketingMaterials mounts. But at this point,
        // the selectedMarketingMaterialsIds are not set yet hence activeMaterial is undefined. And being activeMaterial undefined
        // creates issues. For example, when single MM is selected and the PrintDrawer opens, the button in the footer displays "Next" instead of "Proceed to Checkout".
        setActiveMaterial(materialId ?? selectedMarketingMaterialsIds[0]);
      }}
      bodyStyle={{ padding: 0, paddingTop: 10 }}
      footerStyle={{ textAlign: "right" }}
      footer={
        <Space>
          <Button icon={<PlusOutlined />} onClick={onAdd}>
            Add Marketing Material
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            // onClick={() => onNext(form.getFieldsValue())}
            onClick={() => onNext()}
          >
            {endOfList ? "Proceed to Checkout" : "Next"}
          </Button>
        </Space>
      }
    >
      <Form<MarketingMaterialPrintDeliveryForm>
        form={form}
        layout="horizontal"
        labelAlign="left"
        className={styles.container}
        scrollToFirstError
        initialValues={initialValues}
      >
        <Tabs
          tabBarStyle={{ minHeight: 40 }}
          type="editable-card"
          className={styles.tabs}
          activeKey={activeMaterial ?? selectedMarketingMaterialsIds[0]}
          onChange={key => setActiveMaterial(key)}
          onEdit={(id, action) => {
            switch (action) {
              case "add":
                onAdd();
                break;
              case "remove":
                onDelete(id as string);
                break;
            }
          }}
        >
          {selectedMarketingMaterialsIds.map(marketingMaterialId => {
            const material = marketingMaterials.find(
              ({ id }) => marketingMaterialId === id,
            );
            if (!material) return;

            return (
              <Tabs.TabPane
                key={marketingMaterialId}
                forceRender // this is needed for displaying error message on the tabs that werent visited yet
                tab={
                  <Typography.Text
                    style={
                      confirms[marketingMaterialId] === "invalid"
                        ? { color: "red" }
                        : {}
                    }
                  >
                    {material.name}
                  </Typography.Text>
                }
              >
                <PrintDrawerContainer
                  form={form}
                  materialId={marketingMaterialId}
                />
              </Tabs.TabPane>
            );
          })}
        </Tabs>
        <PrintCheckout form={form} />
      </Form>
      <SelectMarketingMaterialsDrawer
        isAddVisible={isAddVisible}
        onCloseAdd={onCloseAdd}
        onSelectMaterials={onSelectMaterials}
      />
    </Drawer>
  );
};

const PrintDrawerContainer = ({
  form,
  materialId,
}: {
  form: FormInstance<MarketingMaterialPrintDeliveryForm>;
  materialId: string;
}) => {
  const { material, templateFile } = useSelectedMarketingMaterial({
    materialId,
  });

  if (!material) {
    return null;
  }
  const { printOption } = material;
  return (
    <div className={styles.container}>
      <div className={styles.preview}>
        <TemplateRenderDataProvider file={templateFile} material={material}>
          <Preview selectedFile={templateFile} />
        </TemplateRenderDataProvider>
      </div>
      <Form.Item name={["materials", materialId]} style={{ overflow: "auto" }}>
        {printOption && (
          <PrintForm
            form={form}
            materialId={materialId}
            printOption={printOption}
          />
        )}
      </Form.Item>
    </div>
  );
};
